import { Component, OnInit, OnDestroy, Input, ElementRef, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { FileUploader } from 'ng2-file-upload';
import { PDFDocument } from 'pdf-lib';

import { Doc } from '../../../models/doc';

import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { HttpClientService } from '../../../services/common/http-client.service';
import { NotyService } from '../../../services/common/noty.service';

declare var window: any;

@Component({
  selector: 'app-document-form-upload',
  templateUrl: './document-form-upload.component.html',
  styleUrls: ['./document-form-upload.component.css'],
})
export class DocumentFormUploadComponent implements OnInit, OnDestroy {
  /**
   * Matter id from parent component for the document to upload to
   */
  @Input() matter_id: any;

  /**
   * List of selected authors
   * @type {Array<any>}
   */
  authors: Array<any> = [];

  /**
   * Document to upload
   */
  doc: Doc = new Doc();

  /**
   * Indicates if form is being submitted
   * @type {boolean}
   */
  formSubmitting: boolean = false;

  /**
   * Indicates if form has been submitted
   * @type {boolean}
   */
  formSubmitted: boolean = false;

  /**
   * Indicates if form is being resetted
   * @type {boolean}
   */
  resettingForm: boolean = false;

  /**
   * List of tags
   * @type {string}
   */
  tags_list: string;

  /**
   * Indicates if a file is being dragged over the drop zone
   * @type {boolean}
   */
  hasDropZoneOver: boolean = false;

  /**
   * Files selected for upload (supports multiple)
   * @type {File[]}
   */
  selectedFiles: File[] = [];

  /**
   * Indicates if files are being merged
   * @type {boolean}
   */
  merging: boolean = false;

  /**
   * File uploader object from ng2 file upload (used for drag-drop directive)
   * @type {FileUploader}
   */
  uploader: FileUploader;

  /**
   * Upload URL for documents
   * @type {string}
   */
  uploadURL: string = 'documents.json';

  /**
   * File types that can be merged into a PDF
   */
  private mergeableTypes: string[] = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/bmp', 'image/tiff'];

  /**
   * Constructor
   * @param {DocumentStoreService} private documentStoreService Service to store JS objects and share them between components in a page
   * @param {NotyService}          private notyService          Notifications service for web UI
   * @param {HttpClient}           private http                 Angular HttpClient for direct uploads
   * @param {HttpClientService}    private httpClientService    Service with header building utilities
   */
  constructor(
    private documentStoreService: DocumentStoreService,
    private notyService: NotyService,
    private http: HttpClient,
    private httpClientService: HttpClientService,
    private router: Router,
    private elementRef: ElementRef,
    private ngZone: NgZone
  ) {}

  private dropHandler: (e: DragEvent) => void;
  private dragOverHandler: (e: DragEvent) => void;
  private dragLeaveHandler: (e: DragEvent) => void;
  private componentDestroyed: boolean = false;
  private nativeDropSetupTimeout: any;
  private resetTimeout: any;

  // Component lifecycle events

  /**
   * Called on component initialization
   */
  private documentDropHandler: (e: DragEvent) => void;
  private documentDragOverHandler: (e: DragEvent) => void;

  ngOnInit() {
    this.initializeUploader();
    this.setupNativeDropHandler();
    this.setupDocumentLevelDropHandler();
  }

  ngOnDestroy() {
    this.componentDestroyed = true;
    if (this.nativeDropSetupTimeout) clearTimeout(this.nativeDropSetupTimeout);
    if (this.resetTimeout) clearTimeout(this.resetTimeout);

    // Clean up native event listeners
    const dropZone = this.elementRef.nativeElement.querySelector('.drop-zone');
    if (dropZone) {
      dropZone.removeEventListener('drop', this.dropHandler, true);
      dropZone.removeEventListener('dragover', this.dragOverHandler, true);
      dropZone.removeEventListener('dragleave', this.dragLeaveHandler, true);
    }
    // Clean up document-level listeners
    if (this.documentDropHandler) {
      document.removeEventListener('drop', this.documentDropHandler, true);
    }
    if (this.documentDragOverHandler) {
      document.removeEventListener('dragover', this.documentDragOverHandler, true);
    }
  }

  clearDocumentType() {
    (this.doc as any).document_type_id = null;
  }

  /**
   * Document-level drop handler as a last resort — catches drops that
   * Safari doesn't deliver to element-level handlers (e.g. Mail attachments)
   */
  setupDocumentLevelDropHandler() {
    this.documentDragOverHandler = (e: DragEvent) => {
      if (this.componentDestroyed) return;
      // Only prevent default if we're over the drop zone area
      const dropZone = this.elementRef.nativeElement.querySelector('.drop-zone');
      if (dropZone && dropZone.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    this.documentDropHandler = (e: DragEvent) => {
      if (this.componentDestroyed) return;
      const dropZone = this.elementRef.nativeElement.querySelector('.drop-zone');
      if (!dropZone || !dropZone.contains(e.target as Node)) {
        return; // Not our drop zone
      }

      e.preventDefault();
      e.stopPropagation();

      console.log('[drop-document] Document-level drop captured');
      console.log('[drop-document] target:', (e.target as HTMLElement)?.tagName, (e.target as HTMLElement)?.className);

      if (e.dataTransfer) {
        console.log('[drop-document] types:', Array.from(e.dataTransfer.types));
        console.log('[drop-document] files:', e.dataTransfer.files?.length);
        console.log('[drop-document] items:', e.dataTransfer.items?.length);

        const dt = e.dataTransfer;
        this.ngZone.run(() => {
          this.hasDropZoneOver = false;
          let added = 0;

          if (dt.items && dt.items.length > 0) {
            for (let i = 0; i < dt.items.length; i++) {
              const item = dt.items[i];
              console.log(`[drop-document] item[${i}]: kind=${item.kind}, type=${item.type}`);
              if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file && file.size > 0) {
                  this.addFile(file);
                  added++;
                }
              }
            }
          }

          if (added === 0 && dt.files && dt.files.length > 0) {
            for (let i = 0; i < dt.files.length; i++) {
              const file = dt.files[i];
              if (file.size > 0) {
                this.addFile(file);
                added++;
              }
            }
          }

          console.log(`[drop-document] Total added: ${added}`);
        });
      } else {
        console.log('[drop-document] No dataTransfer');
      }
    };

    document.addEventListener('dragover', this.documentDragOverHandler, true);
    document.addEventListener('drop', this.documentDropHandler, true);
    console.log('[drop-document] Document-level handlers installed');
  }

  /**
   * Set up native DOM drop handler on the drop zone element.
   * Uses capture phase (true) to intercept before ng2FileDrop can swallow the event.
   */
  setupNativeDropHandler() {
    this.nativeDropSetupTimeout = setTimeout(() => {
      if (this.componentDestroyed) return;
      const dropZone = this.elementRef.nativeElement.querySelector('.drop-zone');
      if (!dropZone) {
        console.warn('[drop] Could not find .drop-zone element');
        return;
      }

      this.dragOverHandler = (e: DragEvent) => {
        if (this.componentDestroyed) return;
        e.preventDefault();
        this.ngZone.run(() => { this.hasDropZoneOver = true; });
      };

      this.dragLeaveHandler = (e: DragEvent) => {
        if (this.componentDestroyed) return;
        this.ngZone.run(() => { this.hasDropZoneOver = false; });
      };

      this.dropHandler = (e: DragEvent) => {
        if (this.componentDestroyed) return;
        e.preventDefault();
        e.stopPropagation();

        console.log('[drop-native] Drop event captured');

        if (!e.dataTransfer) {
          console.log('[drop-native] No dataTransfer');
          return;
        }

        console.log('[drop-native] types:', Array.from(e.dataTransfer.types));
        console.log('[drop-native] files:', e.dataTransfer.files?.length);
        console.log('[drop-native] items:', e.dataTransfer.items?.length);

        if (e.dataTransfer.items) {
          for (let i = 0; i < e.dataTransfer.items.length; i++) {
            const item = e.dataTransfer.items[i];
            console.log(`[drop-native] item[${i}]: kind=${item.kind}, type=${item.type}`);
          }
        }

        if (e.dataTransfer.files) {
          for (let i = 0; i < e.dataTransfer.files.length; i++) {
            const f = e.dataTransfer.files[i];
            console.log(`[drop-native] file[${i}]: name=${f.name}, size=${f.size}, type=${f.type}`);
          }
        }

        const dt = e.dataTransfer!;
        this.ngZone.run(() => {
          this.hasDropZoneOver = false;
          let added = 0;

          // Try items API first
          if (dt.items && dt.items.length > 0) {
            for (let i = 0; i < dt.items.length; i++) {
              const item = dt.items[i];
              if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file && file.size > 0) {
                  console.log(`[drop-native] Adding from items: ${file.name} (${file.size})`);
                  this.addFile(file);
                  added++;
                }
              }
            }
          }

          // Fallback to files API
          if (added === 0 && dt.files && dt.files.length > 0) {
            for (let i = 0; i < dt.files.length; i++) {
              const file = dt.files[i];
              if (file.size > 0) {
                console.log(`[drop-native] Adding from files: ${file.name} (${file.size})`);
                this.addFile(file);
                added++;
              }
            }
          }

          console.log(`[drop-native] Total added: ${added}`);
        });
      };

      // Use capture phase to get the event before ng2FileDrop
      dropZone.addEventListener('dragover', this.dragOverHandler, true);
      dropZone.addEventListener('dragleave', this.dragLeaveHandler, true);
      dropZone.addEventListener('drop', this.dropHandler, true);
      console.log('[drop-native] Native drop handler installed');
    }, 100);
  }

  // Component methods

  /**
   * Initialize uploader (used for ng2FileDrop directive only)
   */
  initializeUploader() {
    this.uploader = new FileUploader({
      itemAlias: 'uploaded_file',
      url: this.uploadURL
    });
    // When files are added via ng2FileDrop, capture them into selectedFiles
    this.uploader.onAfterAddingFile = (fileItem) => {
      this.addFile(fileItem._file);
      // Clear the uploader queue since we manage files ourselves
      this.uploader.queue = [];
    };
  }

  /**
   * Handle files selected via the native file input
   */
  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        this.addFile(input.files[i]);
      }
    }
    // Reset input so the same file can be re-selected
    input.value = '';
  }

  /**
   * Handle native dragover to allow drop
   */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.hasDropZoneOver = true;
  }

  /**
   * Handle native drop — catches files from Mail and other apps that
   * ng2FileDrop misses (e.g. DataTransferItem-based drops)
   */
  onNativeDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.hasDropZoneOver = false;

    if (!event.dataTransfer) {
      console.log('[drop] No dataTransfer on event');
      return;
    }

    // Debug: log what the browser received
    console.log('[drop] types:', event.dataTransfer.types);
    console.log('[drop] files count:', event.dataTransfer.files?.length);
    console.log('[drop] items count:', event.dataTransfer.items?.length);
    if (event.dataTransfer.items) {
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i];
        console.log(`[drop] item[${i}]: kind=${item.kind}, type=${item.type}`);
      }
    }
    if (event.dataTransfer.files) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        const f = event.dataTransfer.files[i];
        console.log(`[drop] file[${i}]: name=${f.name}, size=${f.size}, type=${f.type}`);
      }
    }

    let added = 0;

    // Try DataTransferItem API first (handles Mail attachments, promised files)
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            console.log(`[drop] Adding file from items: ${file.name} (${file.size} bytes)`);
            this.addFile(file);
            added++;
          }
        }
      }
    }

    // Fallback to DataTransfer.files if items didn't yield anything
    if (added === 0 && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        console.log(`[drop] Adding file from files: ${event.dataTransfer.files[i].name}`);
        this.addFile(event.dataTransfer.files[i]);
        added++;
      }
    }

    console.log(`[drop] Total files added: ${added}`);
  }

  /**
   * Add a file to the selectedFiles list (skip duplicates by name+size)
   */
  addFile(file: File) {
    const isDuplicate = this.selectedFiles.some(
      f => f.name === file.name && f.size === file.size
    );
    if (!isDuplicate) {
      this.selectedFiles.push(file);
    }
  }

  /**
   * Remove a file from the selected files list
   */
  removeFileAt(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  /**
   * Remove all selected files
   */
  removeAllFiles() {
    this.selectedFiles = [];
  }

  /**
   * Reorder a file in the list (move up/down)
   */
  moveFile(index: number, direction: number) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.selectedFiles.length) return;
    const temp = this.selectedFiles[index];
    this.selectedFiles[index] = this.selectedFiles[newIndex];
    this.selectedFiles[newIndex] = temp;
  }

  /**
   * Check if all selected files can be merged (PDF or image)
   */
  canMergeFiles(): boolean {
    return this.selectedFiles.length > 1 &&
      this.selectedFiles.every(f => this.mergeableTypes.includes(f.type));
  }

  /**
   * Get names of files that cannot be merged
   */
  getNonMergeableFiles(): string[] {
    return this.selectedFiles
      .filter(f => !this.mergeableTypes.includes(f.type))
      .map(f => f.name);
  }

  /**
   * Read a file as ArrayBuffer
   */
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Merge multiple PDF/image files into a single PDF
   */
  async mergeFiles(): Promise<File> {
    const mergedPdf = await PDFDocument.create();

    for (const file of this.selectedFiles) {
      const bytes = await this.readFileAsArrayBuffer(file);

      if (file.type === 'application/pdf') {
        const sourcePdf = await PDFDocument.load(bytes);
        const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
        for (const page of pages) {
          mergedPdf.addPage(page);
        }
      } else {
        // Image file — embed as a full page
        let image;
        if (file.type === 'image/png') {
          image = await mergedPdf.embedPng(bytes);
        } else {
          // JPEG, BMP, GIF, TIFF — try as JPEG (browsers typically read these fine)
          image = await mergedPdf.embedJpg(bytes);
        }
        const page = mergedPdf.addPage([image.width, image.height]);
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: image.width,
          height: image.height,
        });
      }
    }

    const mergedBytes = await mergedPdf.save();
    const mergedBlob = new Blob([mergedBytes], { type: 'application/pdf' });
    // Name the merged file after the first file, with .pdf extension
    const baseName = this.selectedFiles[0].name.replace(/\.[^/.]+$/, '');
    return new File([mergedBlob], `${baseName}_merged.pdf`, { type: 'application/pdf' });
  }

  /**
   * Get the total size of all selected files
   */
  getTotalSize(): number {
    return this.selectedFiles.reduce((sum, f) => sum + f.size, 0);
  }

  /**
   * Called on author selection
   * @param {any} item Autocomplete result object
   */
  onAuthorSelect(item: any) {
    for (let author of this.authors) if (author.contact_id == item.id) return;
    item.contact_id = item.id;
    this.authors.push(item);
  }

  /**
   * Remove author from selection
   * @param {number} index Position of author in list
   */
  removeAuthor(index: number) {
    this.authors.splice(index, 1);
  }

  /**
   * Format file size in human-readable format
   * @param {number} bytes File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Called to reset form
   */
  reset() {
    if (this.componentDestroyed) return;
    this.resettingForm = true;
    this.authors = [];
    this.doc = new Doc();
    this.tags_list = '';
    this.selectedFiles = [];
    this.formSubmitted = false;
    let scope: DocumentFormUploadComponent = this;
    this.resetTimeout = window.setTimeout(() => {
      if (scope.componentDestroyed) return;
      scope.uploader.queue = [];
      scope.resettingForm = false;
    }, 500);
  }

  /**
   * Called to upload document using HttpClient with FormData
   * @param {boolean} formValid Indicates if form is valid
   */
  async uploadDocument(formValid: boolean) {
    if (this.componentDestroyed) return;
    this.formSubmitted = true;
    if (this.selectedFiles.length > 0 && formValid && this.doc.document_type_id && this.authors.length > 0 && this.tags_list) {
      this.formSubmitting = true;

      let file: File;

      // If multiple files, merge into one PDF
      if (this.selectedFiles.length > 1) {
        if (!this.canMergeFiles()) {
          const bad = this.getNonMergeableFiles();
          this.notyService.notify(
            `Cannot merge: ${bad.join(', ')} — only PDF and image files can be merged. Remove non-PDF/image files or upload a single file.`,
            'error'
          );
          this.formSubmitting = false;
          return;
        }
        try {
          this.merging = true;
          file = await this.mergeFiles();
          if (this.componentDestroyed) return;
          this.merging = false;
        } catch (err) {
          this.merging = false;
          this.formSubmitting = false;
          console.error('Merge error:', err);
          this.notyService.notify('Error merging files into PDF. Please check that all files are valid.', 'error');
          return;
        }
      } else {
        file = this.selectedFiles[0];
      }

      // Build FormData with all required fields
      const formData = new FormData();
      formData.append('uploaded_file', file, file.name);
      formData.append('upload', 'true');
      formData.append('matter_id', String(this.matter_id));
      formData.append('document_type_id', String(this.doc.document_type_id));

      // Add authors
      for (let author of this.authors) {
        formData.append('author_ids[]', String(author.id));
      }

      // Add authored_at date
      if (this.doc.authored_at_struct) {
        this.doc.authored_at = `${this.doc.authored_at_struct.month}/${this.doc.authored_at_struct.day}/${this.doc.authored_at_struct.year}`;
      }
      if (this.doc.authored_at) {
        formData.append('authored_at', this.doc.authored_at);
      }

      // Add optional fields
      if (this.doc.name) {
        formData.append('name', this.doc.name);
      }
      if (this.doc.notes) {
        formData.append('notes', this.doc.notes);
      }
      if (this.tags_list) {
        formData.append('tag_list', this.tags_list);
      }

      // Use the shared httpClientService to build headers (includes token and intranet ID)
      const headers = this.httpClientService.buildHeaders();

      // Perform the upload using HttpClient
      this.http.post(this.uploadURL, formData, { headers }).subscribe({
        next: (response: any) => {
          if (this.componentDestroyed) return;
          this.formSubmitting = false;
          if (response.success) {
            this.notyService.notify(response.text || 'Document uploaded', 'success');
            this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
            this.reset();
            // If backend returned a redirect URL (e.g., auto redirect rule for receipts), navigate to it
            if (response.redirect_url) {
              this.router.navigateByUrl(response.redirect_url);
            }
          } else {
            this.notyService.notify(response.text || 'Upload failed', 'error');
          }
        },
        error: (error) => {
          if (this.componentDestroyed) return;
          this.formSubmitting = false;
          console.error('Upload error:', error);
          this.notyService.notify('Error uploading document', 'error');
        }
      });
    }
  }
}
